import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { QRCodeSVG } from "qrcode.react";
import { api, gbp } from "@/lib/api";
import { toast } from "sonner";
import { useBranding, resolveAsset } from "@/lib/branding";
import logoGoodBox from "@/assets/logo-goodbox-white.png";
import { startDiag, diagPayload, ackDiag, failDiag } from "@/kiosk/diag";
import {
  ShoppingCart, Plus, Minus, Trash2, X, ArrowLeft, CheckCircle2,
  Loader2, Mail, ChevronRight, Package, XCircle, Lock, PowerOff,
} from "lucide-react";

const VAT_NOTE = "Prices include VAT";

// Serve smaller image variants for fast kiosk loading; leave uploaded/object-storage URLs untouched.
const productImg = (url) => {
  if (!url) return url;
  try {
    if (url.includes("images.unsplash.com")) return url.split("?")[0] + "?auto=format&fit=crop&w=500&q=70";
    if (url.includes("images.pexels.com")) return url.split("?")[0] + "?auto=compress&cs=tinysrgb&fit=crop&w=500&h=500";
  } catch { /* noop */ }
  return url;
};

function KioskImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      {!loaded && <div className="absolute inset-0 bg-stone-100 animate-pulse" />}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? "opacity-100" : "opacity-0"}`}
      />
    </>
  );
}

export default function KioskApp() {
  const { code, token } = useParams();
  const demo = !!token;
  const base = demo ? `/demo/${token}` : `/kiosk/${code}`;
  const [config, setConfig] = useState(null);
  const placeLine = [config?.building_name, config?.location_name].filter(Boolean).join(" · ");
  const [products, setProducts] = useState([]);
  const [screen, setScreen] = useState("idle"); // idle | shop | checkout | success
  const [category, setCategory] = useState("All");
  const [cart, setCart] = useState({}); // id -> qty
  const [session, setSession] = useState(null); // {checkout_url, session_id}
  const [paid, setPaid] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [portrait, setPortrait] = useState(
    typeof window !== "undefined" ? window.matchMedia("(orientation: portrait)").matches : false
  );
  const idleTimer = useRef(null);
  const sessionStart = useRef(Date.now());
  const [closedByAdmin, setClosedByAdmin] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locked = searchParams.get("locked") === "1";
  const [fsLost, setFsLost] = useState(false);
  const holdTimer = useRef(null);
  const [paired, setPaired] = useState(() => demo || (typeof window !== "undefined" ? localStorage.getItem(`hv_paired_${code}`) === "1" : false));
  const wakeLockRef = useRef(null);
  const [dimmed, setDimmed] = useState(false);
  const dimTimer = useRef(null);

  const enterFullscreen = useCallback(() => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
  }, []);

  const exitKiosk = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    navigate("/admin");
  }, [navigate]);

  const startHold = useCallback(() => {
    holdTimer.current = setTimeout(exitKiosk, 2000);
  }, [exitKiosk]);
  const cancelHold = useCallback(() => {
    if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; }
  }, []);

  useEffect(() => {
    if (!locked) return;
    const onFs = () => setFsLost(!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    if (!document.fullscreenElement) enterFullscreen();
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [locked, enterFullscreen]);

  useEffect(() => {
    const mq = window.matchMedia("(orientation: portrait)");
    const on = (e) => setPortrait(e.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [cfg, prods] = await Promise.all([
        api.get(`${base}/config`),
        api.get(`${base}/products`),
      ]);
      setConfig(cfg.data);
      const withVariants = prods.data.map((p) => {
        if (p.is_combo && p.ab_enabled && p.variant_b) {
          if (Math.random() < 0.5) {
            return { ...p, _variant: "B", name: p.variant_b.name || p.name, price_incl_vat: p.variant_b.price_incl_vat, base_price: p.variant_b.base_price };
          }
          return { ...p, _variant: "A" };
        }
        return p.is_combo ? { ...p, _variant: "A" } : p;
      });
      setProducts(withVariants);
    } catch (e) {
      setNotFound(true);
    }
  }, [base]);

  useEffect(() => { loadData(); }, [loadData]);

  // Demo visit tracking (skipped for admin previews opened with ?preview=1)
  const trackDemo = useCallback((event) => {
    if (!demo || searchParams.get("preview") === "1") return;
    const vid = localStorage.getItem("gb_demo_vid") || (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Math.random()).slice(2));
    localStorage.setItem("gb_demo_vid", vid);
    let sid = sessionStorage.getItem(`gb_demo_sid_${token}`);
    if (!sid) { sid = `${vid}-${Date.now()}`; sessionStorage.setItem(`gb_demo_sid_${token}`, sid); }
    const ua = navigator.userAgent || "";
    const touch = navigator.maxTouchPoints || 0;
    const device = /iPad/.test(ua) || (/Macintosh/.test(ua) && touch > 1) ? "iPad / tablet" : /iPhone|Android.*Mobile/.test(ua) ? "Mobile" : /Android/.test(ua) ? "Tablet" : "Desktop";
    api.post(`/demo/${token}/visit`, { visitor_id: vid, session_id: sid, device, event }).catch(() => {});
  }, [demo, token, searchParams]);
  useEffect(() => { trackDemo("open"); }, [trackDemo]);

  // Screen wake lock — keep the tablet awake while the kiosk runs
  useEffect(() => {
    if (notFound) return;
    let released = false;
    const request = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch { /* wake lock unsupported or denied */ }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible" && !released) request();
    };
    request();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (wakeLockRef.current) { wakeLockRef.current.release().catch(() => {}); wakeLockRef.current = null; }
    };
  }, [notFound]);

  const unlockPair = useCallback(() => {
    localStorage.setItem(`hv_paired_${code}`, "1");
    setPaired(true);
  }, [code]);

  // Kiosk heartbeat (online/offline status) + remote close-session signal.
  // Only a PIN-paired terminal reports as online — merely opening the URL in a browser does not.
  useEffect(() => {
    if (!config?.code || closedByAdmin || !paired || demo) return;
    const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    const touch = typeof navigator !== "undefined" ? (navigator.maxTouchPoints || 0) : 0;
    // Detect device kind here (client-side) — an iPad reports a desktop "Macintosh" UA, so maxTouchPoints
    // is the only reliable signal. This hint tells the server it's a tablet, not a computer browser.
    const isTablet = /iPad/.test(ua) || (/Macintosh/.test(ua) && touch > 1) || (/Android/.test(ua) && touch > 1 && !/Mobile/.test(ua));
    let clientHint = "Desktop browser";
    if (isTablet) clientHint = "iPad";
    else if (/iPhone|iPod/.test(ua) || (/Android/.test(ua) && /Mobile/.test(ua))) clientHint = "Mobile";
    const ping = () => {
      const { sent, body } = diagPayload(config.code);
      return api.post(`/kiosk/${config.code}/heartbeat?client=${encodeURIComponent(clientHint)}`, body).then((r) => {
        ackDiag(config.code, sent);
        const rv = r?.data?.revoked_at;
        // A remote "close session" only locks browser sessions (PC/Mac); the real kiosk tablet is never closed.
        if (rv && new Date(rv).getTime() > sessionStart.current && !isTablet) setClosedByAdmin(true);
      }).catch((err) => { if (!err?.response || err.response.status >= 500) failDiag(config.code, err); });
    };
    const stopDiag = startDiag(config.code);
    ping();
    const t = setInterval(ping, 30000);
    return () => { clearInterval(t); stopDiag(); };
  }, [config?.code, closedByAdmin, paired, demo]);

  // Screen dim — after a quiet period, black out the display and release the wake lock so the
  // tablet can power down its backlight (saves heat & battery wear). A single tap wakes it
  // instantly — no PIN, no lock. Never engages during checkout so a paying customer isn't cut off.
  useEffect(() => {
    const dimMins = config?.display?.screen_dim_minutes || 0;
    if (!paired || dimMins <= 0 || screen === "checkout" || closedByAdmin) { setDimmed(false); return; }
    const arm = () => {
      if (dimTimer.current) clearTimeout(dimTimer.current);
      dimTimer.current = setTimeout(() => setDimmed(true), dimMins * 60000);
    };
    const onActivity = () => { setDimmed(false); arm(); };
    arm();
    const events = ["click", "touchstart", "mousemove", "keydown"];
    events.forEach((e) => window.addEventListener(e, onActivity));
    return () => {
      if (dimTimer.current) clearTimeout(dimTimer.current);
      events.forEach((e) => window.removeEventListener(e, onActivity));
    };
  }, [paired, screen, closedByAdmin, config?.display?.screen_dim_minutes]);

  // While dimmed, drop the wake lock so iPadOS can sleep the backlight; re-acquire the moment we wake.
  useEffect(() => {
    if (dimmed) {
      if (wakeLockRef.current) { wakeLockRef.current.release().catch(() => {}); wakeLockRef.current = null; }
    } else if (!notFound && typeof navigator !== "undefined" && "wakeLock" in navigator && !wakeLockRef.current) {
      navigator.wakeLock.request("screen").then((l) => { wakeLockRef.current = l; }).catch(() => {});
    }
  }, [dimmed, notFound]);

  const resetToIdle = useCallback(() => {
    setCart({});
    setSession(null);
    setPaid(false);
    setReceiptEmail("");
    setCartOpen(false);
    setScreen("idle");
    loadData();
  }, [loadData]);

  // Inactivity timer (only on shop screen)
  const timeout = (config?.inactivity_timeout || 60) * 1000;
  useEffect(() => {
    if (screen !== "shop") return;
    const reset = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => resetToIdle(), timeout);
    };
    reset();
    const events = ["click", "touchstart", "mousemove", "keydown"];
    events.forEach((e) => window.addEventListener(e, reset));
    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [screen, timeout, resetToIdle]);

  // Payment polling
  useEffect(() => {
    if (screen !== "checkout" || !session?.session_id || session.demo) return;
    let active = true;
    const poll = setInterval(async () => {
      try {
        const res = await api.get(`/payments/status/${session.session_id}`);
        if (!active) return;
        if (res.data.payment_status === "paid") {
          clearInterval(poll);
          setPaid(true);
          setScreen("success");
        } else if (["failed", "expired"].includes(res.data.payment_status)) {
          clearInterval(poll);
          toast.error(`Payment ${res.data.payment_status}. Please try again.`);
          setSession(null);
          setScreen("shop");
        }
      } catch (e) { /* network hiccup, keep polling */ }
    }, 2500);
    return () => { active = false; clearInterval(poll); };
  }, [screen, session]);

  const categories = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const visible = category === "All" ? products : products.filter((p) => p.category === category);

  const cartItems = Object.entries(cart)
    .map(([id, qty]) => ({ ...products.find((p) => p.id === id), qty }))
    .filter((x) => x.id);
  const subtotalIncl = cartItems.reduce((s, i) => s + i.price_incl_vat * i.qty, 0);
  const vat = cartItems.reduce((s, i) => s + (i.price_incl_vat - i.price_incl_vat / (1 + i.vat_rate / 100)) * i.qty, 0);
  const totalItems = cartItems.reduce((s, i) => s + i.qty, 0);

  const display = config?.display || { grid_cols_landscape: 4, grid_cols_portrait: 2 };
  const cols = portrait ? (display.grid_cols_portrait || 2) : (display.grid_cols_landscape || 4);

  const addToCart = (p) => {
    const cur = cart[p.id] || 0;
    if (p.stock_controlled && cur + 1 > p.stock) {
      toast.error(`Only ${p.stock} in stock`);
      return;
    }
    setCart({ ...cart, [p.id]: cur + 1 });
  };
  const dec = (p) => {
    const cur = cart[p.id] || 0;
    const next = { ...cart };
    if (cur <= 1) delete next[p.id];
    else next[p.id] = cur - 1;
    setCart(next);
  };
  const removeItem = (id) => {
    const next = { ...cart };
    delete next[id];
    setCart(next);
  };

  const checkout = async () => {
    if (!cartItems.length) return;
    if (demo) {
      setSession({ demo: true, session_id: "demo", checkout_url: `${window.location.origin}/demo/${token}` });
      setScreen("checkout");
      return;
    }
    try {
      const items = cartItems.map((i) => ({ product_id: i.id, quantity: i.qty, type: i.is_combo ? "combo" : "product", variant: i._variant || "A" }));
      const res = await api.post("/payments/checkout", {
        kiosk_id: config.id,
        items,
        origin_url: window.location.origin,
      });
      setSession(res.data);
      setScreen("checkout");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
    }
  };

  const sendReceipt = async () => {
    if (!receiptEmail) return;
    if (demo) {
      toast.info("Demo mode: no receipt is sent");
      resetToIdle();
      return;
    }
    try {
      await api.post("/payments/receipt", { session_id: session.session_id, email: receiptEmail });
      toast.success("Receipt on its way!");
      resetToIdle();
    } catch (e) {
      toast.error("Could not send receipt");
    }
  };

  if (notFound)
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-3">
        <XCircle className="h-12 w-12 text-red-400" />
        <p className="text-xl" data-testid="kiosk-not-found">{demo ? "This demo link is no longer active." : `Kiosk "${code}" not found.`}</p>
      </div>
    );

  if (closedByAdmin)
    return (
      <div data-testid="kiosk-closed-screen" className="h-screen w-screen flex flex-col items-center justify-center bg-slate-950 text-white gap-4 px-6 text-center select-none">
        <PowerOff className="h-14 w-14 text-rose-400" />
        <p className="text-2xl font-bold">This browser session was closed</p>
        <p className="text-sm text-white/60 max-w-md">An administrator closed this session remotely because it was left open in a computer browser. It has stopped reporting as online. Your kiosk tablet is unaffected.</p>
        <button
          data-testid="kiosk-closed-resume-button"
          onClick={() => { sessionStart.current = Date.now(); setClosedByAdmin(false); window.location.reload(); }}
          className="mt-2 min-h-[52px] px-8 rounded-2xl bg-white text-slate-900 text-lg font-bold active:scale-[0.99]"
        >
          Start kiosk again
        </button>
      </div>
    );

  return (
    <div className="h-screen w-screen overflow-hidden font-kiosk select-none bg-stone-50 text-stone-900">
      {demo && config && config.show_ribbon !== false && (
        <div data-testid="kiosk-demo-ribbon" className="absolute top-1.5 left-1/2 -translate-x-1/2 z-[80] rounded-full bg-[var(--brand-primary)] text-white text-[10px] font-extrabold tracking-[0.25em] uppercase px-3 py-0.5 shadow-md pointer-events-none">
          Demo
        </div>
      )}
      {config && config.activated === false && (
        <div data-testid="kiosk-draft-banner" className="absolute top-0 inset-x-0 z-[80] bg-amber-500 text-white text-center text-xs sm:text-sm font-extrabold py-1.5 tracking-widest uppercase">
          Draft — this kiosk is not live yet
        </div>
      )}
      {!config && !notFound ? (
        <div className="h-full w-full flex items-center justify-center bg-stone-50">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        </div>
      ) : !paired ? (
        <PinScreen code={code} config={config} onUnlock={unlockPair} />
      ) : (
      <AnimatePresence mode="wait">
        {screen === "idle" && <IdleScreen key="idle" config={config} onStart={() => setScreen("shop")} />}

        {screen === "shop" && (
          <motion.div key="shop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full flex flex-col relative">
            {/* Catalogue */}
            <header className="px-6 py-4 bg-white border-b border-stone-200 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-4 min-w-0">
                {config?.org_logo_url ? <img data-testid="kiosk-customer-logo" src={config.org_logo_url} alt={config?.org_name || "Customer"} className="h-10 w-auto max-w-[160px] object-contain shrink-0" /> : null}
                <div className="min-w-0">
                  {config?.org_name && <h1 className="text-2xl font-extrabold tracking-tight truncate">{config.org_name}</h1>}
                  {placeLine && <p className="text-sm text-stone-500" data-testid="kiosk-place-line">{placeLine}</p>}
                </div>
              </div>
              <button data-testid="kiosk-exit-button" onClick={resetToIdle} className="text-stone-400 hover:text-stone-700 flex items-center gap-1 text-sm">
                <ArrowLeft className="h-4 w-4" /> Cancel
              </button>
            </header>

            <div className="px-6 py-4 flex gap-3 overflow-x-auto no-scrollbar shrink-0">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`kiosk-category-tab-${c.toLowerCase()}`}
                  onClick={() => setCategory(c)}
                  className={`px-6 min-h-[52px] rounded-2xl text-lg font-semibold whitespace-nowrap transition-all ${
                    category === c ? "bg-[var(--brand-primary)] text-white shadow-md" : "bg-white text-stone-600 border border-stone-200"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-28">
              <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {visible.map((p, idx) => {
                  const low = p.stock_controlled && p.stock <= p.low_stock_threshold;
                  return (
                    <motion.div
                      key={p.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: Math.min(idx, 8) * 0.03, duration: 0.25 }}
                      data-testid={`kiosk-product-card-${p.sku}`}
                      className="bg-white rounded-3xl border border-stone-200 overflow-hidden flex flex-col shadow-sm hover:shadow-lg transition-shadow"
                    >
                      <div className="relative aspect-square bg-stone-100 overflow-hidden">
                        {p.image_url ? (
                          <KioskImage src={productImg(p.image_url)} alt={p.name} />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-stone-300"><Package className="h-12 w-12" /></div>
                        )}
                        {p.stock_controlled && (
                          <span className={`absolute top-2 left-2 text-xs font-bold px-2.5 py-1 rounded-full ${low ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                            {low ? `${p.stock} left` : "In stock"}
                          </span>
                        )}
                        {p.is_combo && (
                          <span className="absolute top-2 right-2 text-xs font-extrabold px-2.5 py-1 rounded-full bg-[var(--brand-accent)] text-white shadow">Meal Deal</span>
                        )}
                        {p.happy_hour && (
                          <span className="absolute bottom-2 left-2 text-xs font-extrabold px-2.5 py-1 rounded-full bg-rose-600 text-white shadow">Happy Hour −{p.discount_percent}%</span>
                        )}
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <h3 className="text-lg font-bold leading-tight">{p.name}</h3>
                        {p.description && <p className="text-sm text-stone-500 mt-1 line-clamp-2">{p.description}</p>}
                        {p.is_combo && p.components && (
                          <p className="text-xs text-[var(--brand-primary)] font-semibold mt-1">Includes {p.components.map((c) => `${c.quantity}× ${c.name}`).join(" + ")}</p>
                        )}
                        <div className="mt-auto pt-3 flex items-center justify-between">
                          <div className="flex items-baseline gap-1.5">
                            {p.happy_hour && p.base_price > p.price_incl_vat && (
                              <span className="text-sm text-stone-400 line-through">{gbp(p.base_price)}</span>
                            )}
                            <span className={`text-xl font-extrabold ${p.happy_hour ? "text-rose-600" : "text-stone-900"}`}>{gbp(p.price_incl_vat)}</span>
                          </div>
                          {cart[p.id] ? (
                            <div className="flex items-center gap-2">
                              <button data-testid={`kiosk-product-dec-${p.sku}`} onClick={() => dec(p)} className="h-11 w-11 rounded-xl bg-stone-100 flex items-center justify-center active:scale-95"><Minus className="h-5 w-5" /></button>
                              <span className="w-6 text-center font-bold text-lg">{cart[p.id]}</span>
                              <button data-testid={`kiosk-product-inc-${p.sku}`} onClick={() => addToCart(p)} className="h-11 w-11 rounded-xl bg-[var(--brand-primary)] text-white flex items-center justify-center active:scale-95"><Plus className="h-5 w-5" /></button>
                            </div>
                          ) : (
                            <button
                              data-testid={`kiosk-product-add-${p.sku}`}
                              onClick={() => addToCart(p)}
                              className="h-11 px-5 rounded-xl bg-[var(--brand-primary)] text-white font-semibold flex items-center gap-1.5 active:scale-95 hover:bg-[var(--brand-primary-hover)] transition-colors"
                            >
                              <Plus className="h-5 w-5" /> Add
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
              {!visible.length && <p className="text-center text-stone-400 mt-20">No products available.</p>}
            </div>

            {/* Bottom basket bar */}
            <div className="shrink-0 border-t border-stone-200 bg-white/95 backdrop-blur px-6 py-3">
              <button
                data-testid="kiosk-basket-bar"
                onClick={() => totalItems > 0 && setCartOpen(true)}
                disabled={totalItems === 0}
                className={`w-full min-h-[64px] rounded-2xl px-5 flex items-center justify-between transition-all ${
                  totalItems > 0
                    ? "bg-[var(--brand-primary)] text-white shadow-lg shadow-black/10 active:scale-[0.99] hover:bg-[var(--brand-primary-hover)]"
                    : "bg-stone-100 text-stone-400 cursor-default"
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="relative">
                    <ShoppingCart className="h-6 w-6" />
                    {totalItems > 0 && (
                      <span className="absolute -top-2 -right-2 bg-white text-[var(--brand-primary)] text-xs font-extrabold h-5 min-w-5 px-1 rounded-full flex items-center justify-center">{totalItems}</span>
                    )}
                  </span>
                  <span className="font-bold text-lg">{totalItems > 0 ? "View basket" : "Basket is empty"}</span>
                </span>
                {totalItems > 0 && <span data-testid="kiosk-basket-bar-total" className="font-extrabold text-xl">{gbp(subtotalIncl)}</span>}
              </button>
            </div>

            {/* Slide-in basket drawer */}
            <AnimatePresence>
              {cartOpen && (
                <>
                  <motion.div
                    key="cart-backdrop"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={() => setCartOpen(false)}
                    className="absolute inset-0 bg-black/40 z-40"
                    data-testid="kiosk-cart-backdrop"
                  />
                  <motion.aside
                    key="cart-drawer"
                    initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                    transition={{ type: "tween", duration: 0.28 }}
                    data-testid="kiosk-cart-panel"
                    className="absolute top-0 right-0 h-full w-[380px] xl:w-[440px] max-w-[90vw] bg-white border-l border-stone-200 flex flex-col shadow-2xl z-50"
                  >
                    <div className="px-6 py-5 border-b border-stone-200 flex items-center gap-3">
                      <div className="relative">
                        <ShoppingCart className="h-6 w-6 text-stone-700" />
                        {totalItems > 0 && <span className="absolute -top-2 -right-2 bg-[var(--brand-accent)] text-white text-xs font-bold h-5 w-5 rounded-full flex items-center justify-center">{totalItems}</span>}
                      </div>
                      <h2 className="text-xl font-extrabold">Your Basket</h2>
                      <button data-testid="kiosk-cart-close-button" onClick={() => setCartOpen(false)} className="ml-auto h-10 w-10 rounded-xl bg-stone-100 flex items-center justify-center hover:bg-stone-200"><X className="h-5 w-5" /></button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-3">
                      {cartItems.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-stone-400 gap-2">
                          <ShoppingCart className="h-10 w-10" />
                          <p>Basket is empty</p>
                        </div>
                      ) : (
                        cartItems.map((i) => (
                          <div key={i.id} data-testid={`kiosk-cart-item-${i.sku}`} className="flex gap-3 py-3 border-b border-stone-100">
                            <div className="h-16 w-16 rounded-xl bg-stone-100 overflow-hidden shrink-0">
                              {i.image_url && <img src={productImg(i.image_url)} alt={i.name} loading="lazy" decoding="async" className="w-full h-full object-cover" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate">{i.name}</p>
                              <p className="text-sm text-stone-500">{gbp(i.price_incl_vat)}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <button data-testid={`kiosk-cart-dec-${i.sku}`} onClick={() => dec(i)} className="h-9 w-9 rounded-lg bg-stone-100 flex items-center justify-center"><Minus className="h-4 w-4" /></button>
                                <span className="w-6 text-center font-bold">{i.qty}</span>
                                <button data-testid={`kiosk-cart-inc-${i.sku}`} onClick={() => addToCart(i)} className="h-9 w-9 rounded-lg bg-stone-100 flex items-center justify-center"><Plus className="h-4 w-4" /></button>
                                <button data-testid={`kiosk-cart-remove-${i.sku}`} onClick={() => removeItem(i.id)} className="ml-auto text-stone-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                              </div>
                            </div>
                            <div className="font-bold text-sm">{gbp(i.price_incl_vat * i.qty)}</div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="px-6 py-5 border-t border-stone-200 space-y-2">
                      <div className="flex justify-between text-sm text-stone-500"><span>Subtotal (excl. VAT)</span><span data-testid="kiosk-cart-subtotal">{gbp(subtotalIncl - vat)}</span></div>
                      <div className="flex justify-between text-sm text-stone-500"><span>VAT</span><span data-testid="kiosk-cart-vat">{gbp(vat)}</span></div>
                      <div className="flex justify-between text-2xl font-extrabold pt-1"><span>Total</span><span data-testid="kiosk-cart-total">{gbp(subtotalIncl)}</span></div>
                      <button
                        data-testid="kiosk-cart-checkout-button"
                        disabled={!cartItems.length}
                        onClick={checkout}
                        className="w-full mt-3 min-h-[60px] rounded-2xl bg-[var(--brand-primary)] text-white text-lg font-bold shadow-lg shadow-black/10 disabled:opacity-40 disabled:shadow-none hover:bg-[var(--brand-primary-hover)] transition-colors active:scale-[0.99]"
                      >
                        Pay Now · {gbp(subtotalIncl)}
                      </button>
                      <p className="text-center text-xs text-stone-400">{VAT_NOTE}</p>
                    </div>
                  </motion.aside>
                </>
              )}
            </AnimatePresence>
          </motion.div>
        )}

        {screen === "checkout" && (
          <CheckoutScreen key="checkout" session={session} total={subtotalIncl} onCancel={() => { setSession(null); setScreen("shop"); }} onSimulate={() => { trackDemo("purchase"); setPaid(true); setScreen("success"); }} />
        )}

        {screen === "success" && (
          <SuccessScreen
            key="success"
            config={config}
            cartItems={cartItems}
            total={subtotalIncl}
            vat={vat}
            session={session}
            email={receiptEmail}
            setEmail={setReceiptEmail}
            onSendReceipt={sendReceipt}
            onDone={resetToIdle}
          />
        )}
      </AnimatePresence>
      )}

      {locked && (
        <button
          data-testid="kiosk-lock-exit-button"
          onPointerDown={startHold}
          onPointerUp={cancelHold}
          onPointerLeave={cancelHold}
          title="Hold to exit kiosk mode"
          className="fixed bottom-3 left-3 z-[60] h-10 w-10 rounded-full bg-black/20 text-white/50 flex items-center justify-center backdrop-blur active:bg-black/40"
        >
          <Lock className="h-4 w-4" />
        </button>
      )}

      {locked && fsLost && (
        <div data-testid="kiosk-resume-overlay" className="fixed inset-0 z-[70] bg-slate-950/95 text-white flex flex-col items-center justify-center gap-5 px-6 text-center">
          <Lock className="h-12 w-12 text-white/70" />
          <div>
            <p className="text-2xl font-extrabold">Kiosk locked</p>
            <p className="text-slate-400 mt-1">This terminal is in kiosk mode.</p>
          </div>
          <button
            data-testid="kiosk-resume-button"
            onClick={enterFullscreen}
            className="h-14 px-10 rounded-2xl bg-[var(--brand-primary)] text-white text-lg font-bold active:scale-95 transition-transform"
          >
            Tap to resume
          </button>
          <button
            data-testid="kiosk-resume-exit-button"
            onPointerDown={startHold}
            onPointerUp={cancelHold}
            onPointerLeave={cancelHold}
            className="text-white/40 text-sm underline underline-offset-4"
          >
            Hold to exit kiosk mode
          </button>
        </div>
      )}

      {dimmed && (
        <div
          data-testid="kiosk-dim-overlay"
          onClick={() => setDimmed(false)}
          onTouchStart={() => setDimmed(false)}
          className="fixed inset-0 z-[95] bg-black/80 flex flex-col items-center justify-center gap-3 cursor-pointer select-none text-center px-6"
        >
          <p className="text-white text-4xl sm:text-5xl font-extrabold tracking-tight">Touch to start</p>
          <p className="text-white/70 text-base sm:text-lg">Snacks &amp; drinks · Pay with your phone</p>
          <span data-testid="kiosk-dim-hint" className="mt-4 text-white/40 text-xs tracking-[0.25em] uppercase animate-pulse">Screen dimmed to save power</span>
        </div>
      )}
    </div>
  );
}

function IdleScreen({ config, onStart }) {
  const { branding } = useBranding();
  const brandLogo = branding?.logo_url ? resolveAsset(branding.logo_url) : logoGoodBox;
  const brandName = branding?.brand_name || config?.org_name || "The Good Box";
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="h-full w-full flex flex-col items-center justify-center text-white relative overflow-hidden"
      style={{ background: "var(--brand-kiosk-gradient, linear-gradient(135deg, #4F46E5 0%, #9333EA 100%))" }}
    >
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 30% 30%, white 0, transparent 8%), radial-gradient(circle at 70% 60%, white 0, transparent 6%)" }} />
      <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }} className="relative text-center px-6">
        {brandLogo ? (
          <img src={brandLogo} alt={brandName} className="w-auto mx-auto mb-10" style={{ height: "calc(56px * var(--brand-logo-scale, 1))", maxHeight: "112px" }} />
        ) : (
          <div className="mb-10 font-extrabold tracking-tight text-white" style={{ fontSize: "calc(40px * var(--brand-logo-scale, 1))" }}>{brandName}</div>
        )}
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight">Grab something<br />delicious.</h1>
        <p className="mt-5 text-xl text-white/80">Snacks & drinks · Pay with your phone</p>
        <button
          data-testid="kiosk-idle-tap-start-button"
          onClick={onStart}
          className="relative mt-12 h-40 w-40 mx-auto rounded-full bg-white text-[var(--brand-primary)] text-2xl font-extrabold flex items-center justify-center shadow-2xl pulse-ring active:scale-95 transition-transform"
        >
          Tap to Start
        </button>
        {[config?.building_name, config?.location_name].filter(Boolean).length > 0 && <p className="mt-10 text-white/60 text-sm">{[config?.building_name, config?.location_name].filter(Boolean).join(" · ")}</p>}
        {config?.charity?.show_idle && config?.charity?.name && (
          <div data-testid="kiosk-idle-charity" className="mt-6 inline-block rounded-full bg-white/10 px-4 py-2 text-white/90 text-sm">
            We donate {config.charity.percent}% of every purchase to {config.charity.name}
            {config.charity.raised_total > 0 && <span className="ml-1 font-semibold">— you've helped raise {gbp(config.charity.raised_total)} so far</span>}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

function PinScreen({ code, config, onUnlock }) {
  const { branding } = useBranding();
  const brandLogo = branding?.logo_url ? resolveAsset(branding.logo_url) : logoGoodBox;
  const brandName = branding?.brand_name || "The Good Box";
  // phase: enter | reset_current | new | confirm
  const [phase, setPhase] = useState(config?.pin_required ? "enter" : "new");
  const [pin, setPin] = useState("");
  const [firstPin, setFirstPin] = useState("");
  const [currentPin, setCurrentPin] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [lockMsg, setLockMsg] = useState("");

  const reset = () => { setPin(""); setError(""); };

  const goPhase = (p) => { setPhase(p); setPin(""); setError(""); setLockMsg(""); };

  const submit = async () => {
    if (checking) return;
    setError("");
    setLockMsg("");

    // Verify an existing PIN to activate this terminal
    if (phase === "enter") {
      setChecking(true);
      try {
        const r = await api.post(`/kiosk/${code}/verify-pin`, { pin });
        if (r.data.ok) { onUnlock(); }
        else { setError("Incorrect PIN. Try again."); setPin(""); }
      } catch (e) {
        if (e?.response?.status === 429) { setLockMsg(e.response.data?.detail || "Too many attempts. Try again later."); setPin(""); }
        else { setError("Something went wrong. Try again."); setPin(""); }
      } finally { setChecking(false); }
      return;
    }

    // Verify the current PIN before allowing a reset
    if (phase === "reset_current") {
      setChecking(true);
      try {
        const r = await api.post(`/kiosk/${code}/verify-pin`, { pin });
        if (r.data.ok) { setCurrentPin(pin); goPhase("new"); }
        else { setError("Current PIN is incorrect."); setPin(""); }
      } catch (e) {
        if (e?.response?.status === 429) { setLockMsg(e.response.data?.detail || "Too many attempts. Try again later."); setPin(""); }
        else { setError("Something went wrong. Try again."); setPin(""); }
      } finally { setChecking(false); }
      return;
    }

    // First step of creating a new PIN
    if (phase === "new") {
      if (pin.length < 4) { setError("PIN must be at least 4 digits."); return; }
      setFirstPin(pin); goPhase("confirm");
      return;
    }

    // Confirm the new PIN and save it
    if (phase === "confirm") {
      if (pin !== firstPin) { setError("PINs did not match. Start again."); setFirstPin(""); goPhase("new"); return; }
      setChecking(true);
      try {
        await api.post(`/kiosk/${code}/set-pin`, { pin, current_pin: currentPin || undefined });
        onUnlock();
      } catch (e) {
        if (e?.response?.status === 429) { setLockMsg(e.response.data?.detail || "Too many attempts. Try again later."); }
        else { setError(e?.response?.data?.detail || "Could not save PIN. Try again."); }
        setFirstPin(""); setCurrentPin(""); goPhase(config?.pin_required ? "enter" : "new");
      } finally { setChecking(false); }
      return;
    }
  };

  const press = (d) => { if (checking) return; setPin((p) => (p + d).slice(0, 6)); setError(""); };
  const back = () => { setPin((p) => p.slice(0, -1)); setError(""); };

  const copy = {
    enter: { title: "Enter kiosk PIN to activate", cta: "Activate" },
    reset_current: { title: "Enter the current PIN to reset", cta: "Continue" },
    new: { title: config?.pin_required ? "Choose a new PIN (4-6 digits)" : "Set a PIN for this kiosk (4-6 digits)", cta: "Next" },
    confirm: { title: "Re-enter the new PIN to confirm", cta: "Save PIN" },
  }[phase];

  const canSubmit = pin.length >= 4 && !checking;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="h-full w-full flex flex-col items-center justify-center text-white relative overflow-hidden"
      style={{ background: "var(--brand-kiosk-gradient, linear-gradient(135deg, #1e1b4b 0%, #4F46E5 100%))" }}
      data-testid="kiosk-pin-screen"
    >
      {brandLogo ? (
        <img src={brandLogo} alt={brandName} className="w-auto mb-8" style={{ height: "calc(40px * var(--brand-logo-scale, 1))", maxHeight: "80px" }} />
      ) : (
        <div className="mb-8 font-extrabold tracking-tight text-white" style={{ fontSize: "calc(30px * var(--brand-logo-scale, 1))" }}>{brandName}</div>
      )}
      <p data-testid="kiosk-pin-title" className="text-lg font-semibold px-6 text-center">{copy.title}</p>
      <p className="text-white/60 text-sm mb-6">{[config?.building_name, config?.location_name].filter(Boolean).join(" · ")}</p>

      <div className={`flex gap-3 mb-6 ${error ? "animate-shake" : ""}`} data-testid="kiosk-pin-dots">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`h-4 w-4 rounded-full transition-colors ${i < pin.length ? "bg-white" : "bg-white/25"}`} />
        ))}
      </div>
      {error && <p data-testid="kiosk-pin-error" className="text-rose-300 text-sm mb-4">{error}</p>}
      {lockMsg && <p data-testid="kiosk-pin-locked" className="text-amber-300 text-sm mb-4">{lockMsg}</p>}

      <div className="grid grid-cols-3 gap-4">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button
            key={d}
            data-testid={`kiosk-pin-key-${d}`}
            onClick={() => press(d)}
            className="h-16 w-16 rounded-2xl bg-white/10 hover:bg-white/20 text-2xl font-bold active:scale-95 transition-all"
          >
            {d}
          </button>
        ))}
        <button data-testid="kiosk-pin-clear" onClick={() => setPin("")} className="h-16 w-16 rounded-2xl bg-transparent text-sm text-white/60">Clear</button>
        <button data-testid="kiosk-pin-key-0" onClick={() => press("0")} className="h-16 w-16 rounded-2xl bg-white/10 hover:bg-white/20 text-2xl font-bold active:scale-95 transition-all">0</button>
        <button data-testid="kiosk-pin-back" onClick={back} className="h-16 w-16 rounded-2xl bg-transparent flex items-center justify-center"><X className="h-6 w-6" /></button>
      </div>

      <button
        data-testid="kiosk-pin-submit"
        disabled={!canSubmit}
        onClick={submit}
        className="mt-8 h-14 px-12 rounded-2xl bg-white text-[var(--brand-primary)] text-lg font-extrabold disabled:opacity-40 active:scale-95 transition-transform flex items-center gap-2"
      >
        {checking ? <Loader2 className="h-5 w-5 animate-spin" /> : <Lock className="h-5 w-5" />} {copy.cta}
      </button>

      <div className="mt-6 h-6 flex items-center">
        {phase === "enter" && (
          <button data-testid="kiosk-pin-reset-link" onClick={() => goPhase("reset_current")} className="text-white/60 text-sm underline underline-offset-4">
            Reset PIN
          </button>
        )}
        {(phase === "reset_current" || phase === "confirm" || (phase === "new" && config?.pin_required)) && (
          <button data-testid="kiosk-pin-cancel-link" onClick={() => { setFirstPin(""); setCurrentPin(""); goPhase("enter"); }} className="text-white/60 text-sm underline underline-offset-4">
            Cancel
          </button>
        )}
      </div>
    </motion.div>
  );
}

function CheckoutScreen({ session, total, onCancel, onSimulate }) {
  const [seconds, setSeconds] = useState(600);
  useEffect(() => {
    const t = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, []);
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <motion.div key="checkout" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} className="h-full w-full flex flex-col items-center justify-center bg-slate-950 text-white relative px-6">
      <button data-testid="kiosk-checkout-cancel-button" onClick={onCancel} className="absolute top-6 right-6 h-12 w-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"><X className="h-6 w-6" /></button>
      <h2 className="text-3xl font-extrabold">Scan to Pay {gbp(total)}</h2>
      <p className="mt-2 text-slate-400">Use your phone camera or banking app</p>
      <div className="mt-8 bg-white p-6 rounded-3xl shadow-2xl" data-testid="kiosk-stripe-qr-code">
        {session?.checkout_url && <QRCodeSVG value={session.checkout_url} size={280} level="M" />}
      </div>
      <div className="mt-6 flex items-center gap-2 text-slate-300"><Loader2 className="h-5 w-5 animate-spin" /> Waiting for payment… {mm}:{ss}</div>
      <div className="mt-8 flex flex-col items-center gap-3">
        {session?.demo ? (
          <>
            <p className="text-sm text-slate-500 max-w-md text-center">Demo mode: no real payment is taken. On a live kiosk customers scan this code to pay with Apple Pay, Google Pay or card.</p>
            <button data-testid="kiosk-demo-simulate-payment" onClick={onSimulate} className="min-h-[52px] px-8 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white text-lg font-bold active:scale-[0.99] transition-colors">Simulate payment</button>
          </>
        ) : (
          <p className="text-sm text-slate-500 max-w-md text-center">Supports Apple Pay, Google Pay and card via Stripe. This kiosk detects your payment automatically.</p>
        )}
      </div>
    </motion.div>
  );
}

function SuccessScreen({ config, cartItems, total, vat, session, email, setEmail, onSendReceipt, onDone }) {
  const [countdown, setCountdown] = useState(30);
  const charity = config?.charity;
  const charityAmt = charity?.show_receipt && charity?.percent ? ((total || 0) - (vat || 0)) * charity.percent / 100 : 0;
  useEffect(() => {
    const t = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { if (countdown <= 0) onDone(); }, [countdown, onDone]);

  return (
    <motion.div key="success" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="h-full w-full flex items-center justify-center bg-stone-50 px-6">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="w-full max-w-md bg-white rounded-3xl shadow-xl border border-stone-200 overflow-hidden">
        <div className="bg-emerald-500 text-white p-8 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.1 }}>
            <CheckCircle2 className="h-16 w-16 mx-auto" />
          </motion.div>
          <h2 className="mt-3 text-2xl font-extrabold">Payment Successful</h2>
          <p className="text-white/80 text-sm mt-1">Please collect your items. Thank you!</p>
        </div>
        <div className="p-6">
          <div className="space-y-1.5 max-h-40 overflow-y-auto">
            {cartItems.map((i) => (
              <div key={i.id} className="flex justify-between text-sm">
                <span className="text-stone-600">{i.qty}× {i.name}</span>
                <span className="font-semibold">{gbp(i.price_incl_vat * i.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-3 border-t border-stone-200 space-y-1 text-sm">
            <div className="flex justify-between text-stone-500"><span>VAT included</span><span>{gbp(vat)}</span></div>
            {charityAmt > 0 && <div className="flex justify-between text-emerald-600" data-testid="kiosk-success-charity"><span>To {charity.name}</span><span>{gbp(charityAmt)}</span></div>}
            <div className="flex justify-between text-lg font-extrabold"><span>Total paid</span><span>{gbp(total)}</span></div>
          </div>

          <div className="mt-6">
            <label className="text-sm font-semibold text-stone-600 flex items-center gap-1.5 mb-2"><Mail className="h-4 w-4" /> Email a receipt (optional)</label>
            <div className="flex gap-2">
              <input
                data-testid="kiosk-receipt-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 h-12 rounded-xl border border-stone-200 px-4 outline-none focus:border-[var(--brand-primary)]"
              />
              <button data-testid="kiosk-receipt-send-button" onClick={onSendReceipt} disabled={!email} className="h-12 px-5 rounded-xl bg-[var(--brand-primary)] text-white font-semibold disabled:opacity-40 flex items-center gap-1">Send <ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>

          <button data-testid="kiosk-receipt-skip-button" onClick={onDone} className="w-full mt-4 h-12 rounded-xl bg-stone-100 text-stone-700 font-semibold hover:bg-stone-200 transition-colors">
            Skip & Finish ({countdown}s)
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
